import React, { Component } from "react";
import "./_foundationMultiSelect.scss";

/**
 * Reusable multi-select dropdown for the foundation portal.
 *
 * Props:
 *   label        string   — small label above the control
 *   placeholder  string   — text shown when nothing is selected (default "All")
 *   noun         string   — used in the "N nouns selected" summary (e.g. "chapters")
 *   options      [{ value, label, count?, color? }]
 *   selected     [values] — currently selected option values
 *   onChange     (nextSelectedValues) => void
 *   searchable   bool     — show a search box inside the panel (for long lists)
 *   disabled     bool
 */
class FoundationMultiSelect extends Component {
  state = { open: false, search: "" };

  componentWillUnmount() {
    this.detach();
  }

  // Outside-tap detection that works for both mouse and touch. The listener is
  // attached on the next tick so the very tap that opened the panel can't close
  // it, and it ignores taps that land inside this control.
  onDocClick = (e) => {
    if (this.root && !this.root.contains(e.target)) this.close();
  };
  attach = () => {
    document.addEventListener("mousedown", this.onDocClick);
    document.addEventListener("touchstart", this.onDocClick);
  };
  detach = () => {
    document.removeEventListener("mousedown", this.onDocClick);
    document.removeEventListener("touchstart", this.onDocClick);
  };

  open = () => {
    if (this.props.disabled) return;
    this.setState({ open: true });
    setTimeout(this.attach, 0);
  };
  close = () => {
    this.detach();
    this.setState({ open: false, search: "" });
  };
  toggleOpen = () => (this.state.open ? this.close() : this.open());

  toggleValue = (value) => {
    const selected = this.props.selected || [];
    const next = selected.indexOf(value) === -1
      ? selected.concat(value)
      : selected.filter((v) => v !== value);
    this.props.onChange(next);
  };

  visibleOptions() {
    const q = this.state.search.trim().toLowerCase();
    const opts = this.props.options || [];
    if (!q) return opts;
    return opts.filter((o) => String(o.label).toLowerCase().indexOf(q) !== -1);
  }

  selectAllVisible = () => {
    const vis = this.visibleOptions().map((o) => o.value);
    const selected = this.props.selected || [];
    const allOn = vis.length > 0 && vis.every((v) => selected.indexOf(v) !== -1);
    if (allOn) {
      this.props.onChange(selected.filter((v) => vis.indexOf(v) === -1));
    } else {
      const merged = selected.slice();
      vis.forEach((v) => { if (merged.indexOf(v) === -1) merged.push(v); });
      this.props.onChange(merged);
    }
  };

  clearAll = (e) => {
    e.stopPropagation();
    this.props.onChange([]);
  };

  summary() {
    const selected = this.props.selected || [];
    const placeholder = this.props.placeholder || "All";
    if (!selected.length) return placeholder;
    if (selected.length === 1) {
      const opt = (this.props.options || []).find((o) => o.value === selected[0]);
      return opt ? opt.label : `1 ${this.props.noun || "selected"}`;
    }
    return `${selected.length} ${this.props.noun || "selected"}`;
  }

  render() {
    const { label, options = [], selected = [], searchable, disabled } = this.props;
    const { open, search } = this.state;
    const vis = this.visibleOptions();
    const allVisOn = vis.length > 0 && vis.every((v) => selected.indexOf(v.value) !== -1);
    const hasSel = selected.length > 0;

    return (
      <div className={`fms ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`} ref={(el) => (this.root = el)}>
        {label ? <div className="fms-label">{label}</div> : null}

        <button type="button" className="fms-control" onClick={this.toggleOpen} disabled={disabled}>
          <span className={`fms-value ${hasSel ? "" : "is-placeholder"}`}>{this.summary()}</span>
          {hasSel ? (
            <span className="fms-clear" role="button" title="Clear" onClick={this.clearAll}>
              <i className="fas fa-times" />
            </span>
          ) : null}
          <i className={`fas fa-chevron-${open ? "up" : "down"} fms-caret`} />
        </button>

        {open ? (
          <div className="fms-panel">
            {searchable ? (
              <div className="fms-search">
                <i className="fas fa-search" />
                <input
                  autoFocus
                  type="text"
                  value={search}
                  placeholder="Search…"
                  onChange={(e) => this.setState({ search: e.target.value })}
                />
              </div>
            ) : null}

            <div className="fms-actions">
              <button type="button" onClick={this.selectAllVisible}>
                {allVisOn ? "Clear shown" : "Select shown"}
              </button>
              <span className="fms-selcount">{selected.length} selected</span>
            </div>

            <div className="fms-list">
              {vis.length ? vis.map((o) => {
                const on = selected.indexOf(o.value) !== -1;
                return (
                  <button
                    type="button"
                    key={String(o.value)}
                    className={`fms-opt ${on ? "is-on" : ""}`}
                    onClick={() => this.toggleValue(o.value)}
                    title={o.label}
                  >
                    <span className="fms-check">{on ? <i className="fas fa-check" /> : null}</span>
                    {o.color ? <span className="fms-dot" style={{ background: o.color }} /> : null}
                    <span className="fms-opt-label">{o.label}</span>
                    {o.count != null ? <span className="fms-opt-count">{o.count}</span> : null}
                  </button>
                );
              }) : <div className="fms-empty"><i className="fas fa-search" />No matches.</div>}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default FoundationMultiSelect;
